import { defineTour } from '../../types';
import { PERMISSIONS } from './access';

/**
 * T07 — Receive a delivery and record exceptions (brief §7).
 *
 * For a seat that sees the receiving queue (`receiving.receipt.view`, as the
 * Receiving entry is). Recording and closing short are separate permissions
 * held by different roles (R-033 FLW-22), and the steps follow them exactly as
 * the receipt page does. The queue is `/receiving` (GET only). The receipt
 * page is an entry form with a one-press "everything arrived" — never
 * navigated to (safeRoutes: EXCLUDED) — so its steps run only once the person
 * has opened an order from the queue. No stock or payment is implied.
 *
 * Copy: `guidance.tours.T07.*` — draft, for native-language review.
 */
const RECORD = {
  routeId: 'tenant.receive',
  explainKey: 'guidance.tours.T07.openOrder',
} as const;

export const T07 = defineTour({
  id: 'T07',
  version: 1,
  portal: 'tenant',
  titleKey: 'guidance.tours.T07.title',
  summaryKey: 'guidance.tours.T07.summary',
  eligible: (ctx) => ctx.portal === 'tenant' && ctx.can(PERMISSIONS.receiptView),
  pageRoutes: ['tenant.receiving', 'tenant.receive'],
  steps: [
    {
      id: 't07-queue',
      anchor: 'tenant.receiving.queue',
      route: 'tenant.receiving',
      titleKey: 'guidance.tours.T07.queue.title',
      bodyKey: 'guidance.tours.T07.queue.body',
    },
    {
      id: 't07-facts',
      anchor: 'tenant.receive.facts',
      needsRecord: RECORD,
      titleKey: 'guidance.tours.T07.facts.title',
      bodyKey: 'guidance.tours.T07.facts.body',
    },
    {
      id: 't07-lines',
      anchor: 'tenant.receive.lines',
      needsRecord: RECORD,
      when: (ctx) => ctx.can(PERMISSIONS.receiptRecord),
      titleKey: 'guidance.tours.T07.lines.title',
      bodyKey: 'guidance.tours.T07.lines.body',
    },
    {
      id: 't07-delivery',
      anchor: 'tenant.receive.delivery-details',
      needsRecord: RECORD,
      when: (ctx) => ctx.can(PERMISSIONS.receiptRecord),
      titleKey: 'guidance.tours.T07.delivery.title',
      bodyKey: 'guidance.tours.T07.delivery.body',
    },
    {
      id: 't07-history',
      anchor: 'tenant.receive.history',
      needsRecord: RECORD,
      titleKey: 'guidance.tours.T07.history.title',
      bodyKey: 'guidance.tours.T07.history.body',
    },
    {
      // Opens its own confirmation with a reason; explained, never pressed.
      id: 't07-close-short',
      anchor: 'tenant.receive.close-short',
      needsRecord: RECORD,
      when: (ctx) => ctx.can(PERMISSIONS.receiptCloseShort),
      titleKey: 'guidance.tours.T07.closeShort.title',
      bodyKey: 'guidance.tours.T07.closeShort.body',
    },
    {
      // Holds the one-press "Everything arrived correctly": the guide ends here.
      id: 't07-record',
      anchor: 'tenant.receive.commit',
      needsRecord: RECORD,
      when: (ctx) => ctx.can(PERMISSIONS.receiptRecord),
      titleKey: 'guidance.tours.T07.record.title',
      bodyKey: 'guidance.tours.T07.record.body',
      commitNote: true,
    },
  ],
});
